import { useTranslation } from 'react-i18next';
import { Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';

export interface ReportFilterState {
  dateFrom: string;
  dateTo: string;
  warehouseId: string;
  brandId: string;
  type: string;
  search: string;
  sortBy: 'dateVoucher' | 'product';
}

export function defaultReportFilters(): ReportFilterState {
  const now = new Date();
  const from = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { dateFrom: iso(from), dateTo: iso(now), warehouseId: '', brandId: '', type: '', search: '', sortBy: 'dateVoucher' };
}

// Mirrors the server TYPE_LABEL_TR (stockReports.ts).
const MOVEMENT_TYPES: { value: string; label: string }[] = [
  { value: 'INITIAL_STOCK', label: 'Devreden giriş' },
  { value: 'PURCHASE_RECEIPT', label: 'Ambara giriş (Alış)' },
  { value: 'SALES_ISSUE', label: 'Satış' },
  { value: 'RETURN_FROM_CUSTOMER', label: 'Satış iade' },
  { value: 'RETURN_TO_SUPPLIER', label: 'Satıcıya iade' },
  { value: 'TRANSFER_IN', label: 'Transfer giriş' },
  { value: 'TRANSFER_OUT', label: 'Transfer çıkış' },
  { value: 'ADJUSTMENT_IN', label: 'Sayım fazlası' },
  { value: 'ADJUSTMENT_OUT', label: 'Sayım noksanı' },
  { value: 'SCRAP', label: 'Sarf (Fire)' },
  { value: 'CONSIGNMENT_OUT', label: 'Konsinye çıkış' },
  { value: 'CONSIGNMENT_RETURN', label: 'Konsinye iade' },
  { value: 'CONSIGNMENT_SOLD', label: 'Konsinye satış' },
];

const selectClass =
  'h-8 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

interface Props {
  value: ReportFilterState;
  onChange: (v: ReportFilterState) => void;
  show?: { warehouse?: boolean; brand?: boolean; type?: boolean; sort?: boolean; search?: boolean };
  onExport: () => void;
  exporting: boolean;
  exportDisabled?: boolean;
}

export function ReportFilters({ value, onChange, show = {}, onExport, exporting, exportDisabled }: Props) {
  const { t } = useTranslation('trading');
  const set = (patch: Partial<ReportFilterState>) => onChange({ ...value, ...patch });

  const warehouses = trpc.warehouse.list.useQuery(undefined, { enabled: !!show.warehouse });
  const brands = trpc.tradingBrand.list.useQuery({ page: 1, limit: 200 }, { enabled: !!show.brand });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <div className="space-y-1">
        <Label className="text-xs">{t('reports.dateFrom', 'Başlangıç')}</Label>
        <Input type="date" value={value.dateFrom} onChange={(e) => set({ dateFrom: e.target.value })} className="w-36" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t('reports.dateTo', 'Bitiş')}</Label>
        <Input type="date" value={value.dateTo} onChange={(e) => set({ dateTo: e.target.value })} className="w-36" />
      </div>

      {show.warehouse && (
        <div className="space-y-1">
          <Label className="text-xs">{t('reports.department', 'Departman')}</Label>
          <select value={value.warehouseId} onChange={(e) => set({ warehouseId: e.target.value })} className={`${selectClass} block w-44`}>
            <option value="">{t('reports.all', 'Tümü')}</option>
            {(warehouses.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      )}

      {show.brand && (
        <div className="space-y-1">
          <Label className="text-xs">{t('reports.brand', 'Marka')}</Label>
          <select value={value.brandId} onChange={(e) => set({ brandId: e.target.value })} className={`${selectClass} block w-40`}>
            <option value="">{t('reports.all', 'Tümü')}</option>
            {(brands.data?.brands ?? []).map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {show.type && (
        <div className="space-y-1">
          <Label className="text-xs">{t('reports.movementType', 'Fiş Tipi')}</Label>
          <select value={value.type} onChange={(e) => set({ type: e.target.value })} className={`${selectClass} block w-44`}>
            <option value="">{t('reports.all', 'Tümü')}</option>
            {MOVEMENT_TYPES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      )}

      {show.sort && (
        <div className="space-y-1">
          <Label className="text-xs">{t('reports.sortBy', 'Sıralama')}</Label>
          <select value={value.sortBy} onChange={(e) => set({ sortBy: e.target.value as ReportFilterState['sortBy'] })} className={`${selectClass} block w-40`}>
            <option value="dateVoucher">{t('reports.sortDateVoucher', 'Tarih / Fiş No')}</option>
            <option value="product">{t('reports.sortProduct', 'Ürün')}</option>
          </select>
        </div>
      )}

      {show.search && (
        <div className="space-y-1">
          <Label className="text-xs">{t('reports.search', 'Ara')}</Label>
          <Input value={value.search} onChange={(e) => set({ search: e.target.value })} placeholder={t('reports.searchPlaceholder', 'Ürün, kod, fiş...')} className="w-52" />
        </div>
      )}

      <div className="ml-auto">
        <Button variant="outline" onClick={onExport} disabled={exporting || exportDisabled}>
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          {t('reports.export', 'Dışa Aktar')}
        </Button>
      </div>
    </div>
  );
}
